import { useState, useRef, useMemo } from "react";
import IconButton from "../../../components/ui/icon-button";
import { Plus, X } from "lucide-react";
import { type Tag } from "tag-api-client";
import {
    parseKeyValue,
    //tagSchema,
    type TagParts,
} from "@/features/tags/utils/tag-utils";
import { cn } from "@/lib/utils";

interface InlineTagInputProps {
    availableTags: Tag[];
    currentTags: TagParts[];
    onAddTag: (tag: TagParts) => void;
    disabled?: boolean;
}

export function InlineTagInput({
    availableTags,
    currentTags,
    onAddTag,
    disabled = false,
}: InlineTagInputProps) {
    const [isInputMode, setIsInputMode] = useState(false);
    const [inputValue, setInputValue] = useState("");
    const [open, setOpen] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);

    // Group tags by key
    const groupedTags = useMemo(() => {
        const groups = new Map<string, TagParts[]>();
        availableTags.forEach((tag) => {
            const parsed = parseKeyValue(tag.keyValue);
            if (
                parsed &&
                !currentTags.some(
                    (ct) => ct.key === parsed.key && ct.value === parsed.value,
                )
            ) {
                if (!groups.has(parsed.key)) groups.set(parsed.key, []);
                groups.get(parsed.key)!.push(parsed);
            }
        });
        return Array.from(groups.entries()).sort(([a], [b]) =>
            a.localeCompare(b),
        );
    }, [availableTags, currentTags]);

    // Filter by search input
    const filteredGroupedTags = useMemo(() => {
        if (!inputValue) return groupedTags;
        const lower = inputValue.toLowerCase();
        return groupedTags
            .map(([key, tags]) => {
                const filtered = tags.filter(
                    (tag) =>
                        key.toLowerCase().includes(lower) ||
                        tag.value.toLowerCase().includes(lower),
                );
                return [key, filtered] as [string, TagParts[]];
            })
            .filter(([, tags]) => tags.length > 0);
    }, [groupedTags, inputValue]);

    const handleAddTag = (tag: TagParts) => {
        onAddTag(tag);
        setInputValue("");
        setOpen(false);
        setIsInputMode(false);
    };

    const handleCancel = () => {
        setInputValue("");
        setOpen(false);
        setIsInputMode(false);
    };

    if (isInputMode) {
        return (
            <div className="flex items-center gap-1">
                <div className="relative min-w-48">
                    <input
                        ref={inputRef}
                        type="text"
                        placeholder="Search tags..."
                        value={inputValue}
                        onChange={(e) => setInputValue(e.target.value)}
                        onFocus={() => setOpen(true)}
                        onBlur={(e) => {
                            if (
                                !e.relatedTarget?.closest(
                                    "[data-popover-content]",
                                )
                            ) {
                                handleCancel();
                            }
                        }}
                        autoFocus
                        className="w-full h-10 px-2.5 rounded-[var(--color-radius)] border border-md-outline bg-md-surface text-md-on-surface text-sm outline-none focus:border-md-primary focus:ring-[3px] focus:ring-md-primary/20 transition-all hover:border-md-primary hover:bg-md-tertiary-container/10 placeholder:text-gray-600"
                    />
                    {open && (
                        <div
                            data-popover-content
                            className="absolute top-full left-0 mt-1.5 min-w-80 max-w-md max-h-96 overflow-hidden rounded-lg border border-md-outline-variant bg-md-surface shadow-md z-50 animate-in fade-in-0 zoom-in-95 slide-in-from-top-2 duration-200"
                            onMouseDown={(e) => e.preventDefault()}
                        >
                            <div className="max-h-96 overflow-y-auto p-1">
                                {filteredGroupedTags.length === 0 ? (
                                    <div className="py-6 text-center text-sm text-md-on-surface-variant">
                                        No tags found
                                    </div>
                                ) : (
                                    filteredGroupedTags.map(([key, tags]) => (
                                        <div key={key}>
                                            <div className="px-2.5 py-1.5 text-xs font-bold text-md-on-surface bg-md-surface-level-1 -mx-1 mb-0.5">
                                                {key}
                                            </div>
                                            {tags.map((tag) => (
                                                <button
                                                    key={`${tag.key}:${tag.value}`}
                                                    onClick={() =>
                                                        handleAddTag(tag)
                                                    }
                                                    className={cn(
                                                        "relative flex w-full items-center gap-2 rounded-md py-1 pr-8 pl-1.5 text-sm text-left outline-none select-none cursor-pointer",
                                                        "hover:bg-md-primary-container hover:text-md-on-primary-container transition-colors duration-150",
                                                    )}
                                                >
                                                    {tag.value}
                                                </button>
                                            ))}
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>
                    )}
                </div>
                <IconButton
                    onClick={handleCancel}
                    size="sm"
                    variant="ghost"
                    color="neutral"
                >
                    <X className="h-4 w-4" />
                </IconButton>
            </div>
        );
    }

    return (
        <IconButton
            onClick={() => setIsInputMode(true)}
            size="sm"
            color="primary"
            variant="outlined"
            disabled={disabled}
        >
            <Plus className="h-4 w-4" />
        </IconButton>
    );
}
